<!-- A pair of arrows (left/right) for scrolling an overflowing container horizontally as an alternative to using OS native scrollbars. -->
<!-- Placement of these arrows is left up to the consumer but they should typically be attached to top right above the overflowing container. -->
<template>
  <div v-if="!leftChevronDisabled || !rightChevronDisabled" class="f bg-bg br-pill">
    <lucide-icon
      name="ChevronLeft"
      :class="{
        'light-gray': leftChevronDisabled,
        'c-p': !leftChevronDisabled
      }"
      :size="20"
      :stroke-width="2"
      @click="() => scrollTableHorizontally(-1)"
    />
    <lucide-icon
      name="ChevronLeft"
      class="rot-180"
      :class="{
        'light-gray': rightChevronDisabled,
        'c-p': !rightChevronDisabled
      }"
      :size="20"
      :stroke-width="2"
      @click="() => scrollTableHorizontally(1)"
    />
  </div>
</template>

<script setup>
 import { watch, onMounted, onUnmounted, ref } from 'vue';

 const leftChevronDisabled = ref(null);
 const rightChevronDisabled = ref(null);

 const props = defineProps({
   scrollingElementRef: {
     type: Object,
     default: null,
   },
 });

 const scrollTableHorizontally = (direction) => {
   const scrollingElement = props.scrollingElementRef;
   const newLeft = scrollingElement.scrollLeft
                 + scrollingElement.clientWidth * 0.98 // scroll 98% of element width (this would scroll almost a full "page" if there's enough room)
                 * direction; // can be +1 or -1 for left or right
   scrollingElement.scrollTo({ left: newLeft, behavior: 'smooth' });
 };

 const updateChevrons = () => {
   const scrollingElement = props.scrollingElementRef;
   const threshold = 10;
   leftChevronDisabled.value = scrollingElement.scrollLeft < threshold;
   rightChevronDisabled.value = scrollingElement.scrollWidth - scrollingElement.clientWidth - scrollingElement.scrollLeft < threshold;
 };

 const handleScroll = () => {
   updateChevrons();
 };

 const handleResize = () => {
   if (props.scrollingElementRef) {
     updateChevrons();
   }
 };

 onMounted(() => {
   window.addEventListener('resize', handleResize);

   // During dev, this ensures that correct event listeners are attached and the state is up to date
   if (props.scrollingElementRef) {
     updateChevrons();
     props.scrollingElementRef.addEventListener('scroll', handleScroll);
   }
 });

 watch(() => props.scrollingElementRef, (newRef, oldRef) => {
   if (oldRef) { // ref has changed from non-null to non-null; presumably now pointing to a different element?
     oldRef.removeEventListener('scroll', handleScroll);
   }

   if (newRef) {
     newRef.addEventListener('scroll', handleScroll);

     // trigger updateChevrons on mount too such that chevrons disabled state gets initialized
     updateChevrons();
   }
 });

 onUnmounted(() => {
   if (props.scrollingElementRef) {
     props.scrollingElementRef.removeEventListener('scroll', handleScroll);
   }

   window.removeEventListener('resize', handleResize);
 });
</script>
